import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import type { MindMapLayout, MindNode } from "@/lib/tool-kit/diagrams/mind-map";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A mind map from `layoutMindMap`. The central idea is a dark box with white text; each
 * main branch is a box filled with the branch's colour; the ideas below are white boxes
 * outlined in it, and the connecting curves are drawn in it too, so a branch reads as
 * one colour from the centre outward. Curves are drawn first, under every box.
 */

function nodeColors(node: MindNode, branchColor: string) {
  if (node.depth === 0) {
    return { fill: CHART_INK.text, stroke: CHART_INK.text, text: "#ffffff" };
  }
  if (node.depth === 1) {
    return {
      fill: branchColor,
      stroke: branchColor,
      text: textColorOn(branchColor),
    };
  }
  return {
    fill: CHART_INK.background,
    stroke: branchColor,
    text: CHART_INK.text,
  };
}

export function MindMapDiagram({
  id,
  layout,
  branchColors,
  label,
  description,
}: {
  id: string;
  layout: MindMapLayout;
  /** One colour per main branch. */
  branchColors: readonly string[];
  label: string;
  description: string;
}) {
  const colorOf = (node: MindNode) =>
    branchColors[node.branch] ?? CHART_INK.axis;
  const byId = new Map(layout.nodes.map((node) => [node.id, node]));
  return (
    <ChartSvg
      description={description}
      height={layout.height}
      id={id}
      label={label}
      width={layout.width}
    >
      <ChartTitle title={layout.title} />
      {layout.links.map((link) => {
        const child = byId.get(link.to);
        const [x1, y1, c1x, c1y, c2x, c2y, x2, y2] = link.points.map(px);
        return (
          <path
            d={`M${x1} ${y1}C${c1x} ${c1y} ${c2x} ${c2y} ${x2} ${y2}`}
            fill="none"
            key={`link-${link.from}-${link.to}`}
            stroke={child ? colorOf(child) : CHART_INK.axis}
            strokeLinecap="round"
            strokeWidth={child && child.depth === 1 ? 3 : 2}
          />
        );
      })}
      {layout.nodes.map((node) => {
        const colors = nodeColors(node, colorOf(node));
        const lineHeight = node.fontSize * LINE_HEIGHT;
        const height = node.lines.length * lineHeight;
        return (
          <g key={`node-${node.id}`}>
            <rect
              fill={colors.fill}
              height={px(node.rect.height)}
              rx={node.depth === 0 ? 10 : 6}
              stroke={colors.stroke}
              strokeWidth={node.depth >= 2 ? 1.75 : 1}
              width={px(node.rect.width)}
              x={px(node.rect.x)}
              y={px(node.rect.y)}
            />
            <TextLines
              bold={node.bold}
              fill={colors.text}
              fontSize={node.fontSize}
              lineHeight={lineHeight}
              lines={node.lines}
              top={node.rect.y + (node.rect.height - height) / 2}
              x={node.rect.x + node.rect.width / 2}
            />
          </g>
        );
      })}
    </ChartSvg>
  );
}
